/**
 * Phase 640 — Video Analysis list page.
 * Shows every clip this academy's coaches have shared, sorted newest
 * first. "New analysis" button opens the studio.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Play, Trash2, Share2, Users, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function VideoAnalysisListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/video-analyses');
      setRows(r.data.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load video analyses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this video analysis? Parents with the share link will no longer be able to view it.')) return;
    try {
      await api.delete(`/video-analyses/${id}`);
      toast.success('Deleted');
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  }, [fetchList]);

  return (
    <div className="space-y-6" data-testid="video-analysis-list">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> Video Analysis
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Record a clip · draw arrows · send to the parent on WhatsApp. Closes the loop coaches used to lose over text.
          </p>
        </div>
        <Link to="/video-analysis/new">
          <Button data-testid="new-video-analysis-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New analysis
          </Button>
        </Link>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!loading && rows.length === 0 && (
        <Card className="border-dashed" data-testid="video-analysis-empty">
          <CardContent className="p-8 text-center">
            <Play className="h-8 w-8 text-slate-400 mx-auto mb-2" />
            <p className="font-medium">No analyses yet</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Record a 30-second clip of a player, mark the moment you want to talk about, and send it. Parents love this — it takes coaching from &ldquo;told&rdquo; to &ldquo;shown&rdquo;.
            </p>
            <Link to="/video-analysis/new">
              <Button className="mt-4" data-testid="empty-new-btn">Record your first clip →</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map(row => (
            <Card key={row.id} className="hover:shadow-md transition-shadow"
                  data-testid={`video-row-${row.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold truncate">{row.title}</h3>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Users className="h-3 w-3" /> {row.student_name}
                    </p>
                  </div>
                  {row.share_token && (
                    <Badge variant="outline" className="text-[10px]">
                      <Share2 className="h-3 w-3 mr-0.5" /> Shared
                    </Badge>
                  )}
                </div>
                {row.comment && (
                  <p className="text-xs text-slate-600 line-clamp-2">{row.comment}</p>
                )}
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-[10px] font-mono text-slate-400">
                    {new Date(row.created_at).toLocaleDateString()} · {(row.annotations || []).length} annotations
                  </span>
                  <div className="flex gap-1">
                    <Link to={`/video-analysis/${row.id}`}>
                      <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`open-${row.id}`}>
                        Open
                      </Button>
                    </Link>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(row.id)}
                            className="h-7 text-xs text-red-600 hover:bg-red-50"
                            data-testid={`delete-${row.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
